import { useEffect, useMemo, useState } from 'react';
import { getRails, type RailOption } from '../../services/rails.service';
import { SAVE_ERROR_MODE_TERMINAL } from '../../constants/saveErrorSources';

/**
 * Mode/Terminal compatibility hard-block (V1 parity — the onsave guard in
 * lib_editrecord.pl). On an IMPORT/EXPORT move, Mode must be AWR for a port terminal
 * and MLB for a rail one; HOU-APM was grandfathered out of the V1 check and stays out.
 *
 * Lives HERE, called from ProDetail, rather than in OrderFormView — where it was —
 * because a hard block must not depend on which tab is open (bugfix #33440). Two ways
 * that bit:
 *   - Order Info unmounts on a tab switch, and the effect's cleanup cleared the block
 *     with it. Switch tabs and the mismatch saved.
 *   - It read Mode from the SAVED details while reading Terminal from the edited value,
 *     so fixing Mode on the Tracing tab left the block standing on a stale value —
 *     blocking the very save that would have resolved it.
 * Both fields are read edited-first here, and ProDetail is mounted for the whole
 * drawer session, so the block tracks the live values from any tab.
 *
 * The rails lookup is session-cached in the service; a failed fetch leaves the map
 * empty, which skips the check rather than blocking on incomplete data (the same
 * degrade OrderFormView's dropdown does).
 */
export function useModeTerminalSaveGuard(
  details: { moveType?: string | null; rail?: string | null; traceMode?: string | null } | null,
  editedOrderFields: Record<string, unknown> | undefined,
  setExtraSaveErrors: ((source: string, errors: string[]) => void) | undefined,
): void {
  const [rails, setRails] = useState<readonly RailOption[]>([]);
  useEffect(() => {
    let cancelled = false;
    getRails()
      .then((rows) => { if (!cancelled) setRails(rows); })
      .catch(() => { /* empty map -> check skipped */ });
    return () => { cancelled = true; };
  }, []);
  const railTerminalType = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of rails) map.set(r.terminal, r.terminalType);
    return map;
  }, [rails]);

  const edited = editedOrderFields ?? {};
  const pick = (key: 'rail' | 'traceMode'): string =>
    String((edited[key] !== undefined ? edited[key] : details?.[key]) ?? '');
  const effectiveRail = pick('rail');
  const effectiveMode = pick('traceMode');
  const moveType = String(details?.moveType ?? '');

  useEffect(() => {
    if (!setExtraSaveErrors) return;
    const isImportOrExport = moveType === 'IMPORT' || moveType === 'EXPORT';
    const terminalType = railTerminalType.get(effectiveRail) ?? '';
    const portModeMismatch = effectiveMode === 'MLB' && terminalType === 'PORT';
    const railModeMismatch = effectiveMode === 'AWR' && terminalType === 'RAIL';
    const failsCheck =
      isImportOrExport &&
      effectiveRail !== 'HOU-APM' &&
      (portModeMismatch || railModeMismatch);
    // Always write — an empty list is how the block lifts once the pair is valid, or
    // when the drawer moves to a different pro. No unmount cleanup: this hook outlives
    // the tabs, and clearing on ITS unmount is what let the mismatch through.
    setExtraSaveErrors(
      SAVE_ERROR_MODE_TERMINAL,
      failsCheck
        ? ['Mode or Terminal incorrectly set. Mode must be AWR for ports or MLB for rails.']
        : [],
    );
  }, [setExtraSaveErrors, effectiveRail, effectiveMode, moveType, railTerminalType]);
}
